import { X } from 'lucide-react';
import type { MoneyFlowEvent } from '@/types/database';

interface EventDetailModalProps {
  event: MoneyFlowEvent;
  bankRate: number;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  canEdit: boolean;
}

const fmt = (n: number, d = 2) =>
  n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });

export default function EventDetailModal({ event, bankRate, onClose, onEdit, onDelete, canEdit }: EventDetailModalProps) {
  const lkrReceived = (event.wise_usd_sent - event.fee_usd) * event.exchange_rate_lkr_usd;
  const usdtBought = event.p2p_rate_lkr_usdt > 0 ? lkrReceived / event.p2p_rate_lkr_usdt : 0;
  const mvrOut = event.usdt_sent * event.mvr_rate_per_usdt;
  const profit = mvrOut - (event.wise_usd_sent * bankRate);

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <div className="glass-modal rounded-2xl max-w-md w-full">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div>
            <h2 className="text-sm font-bold text-white">Event Details</h2>
            <p className="text-[10px] text-white mt-0.5">{event.event_date} &middot; {event.business_name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-white p-1 glass-hover rounded-lg"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-4 py-3 space-y-3">
          <div>
            <p className="text-[9px] font-bold text-white uppercase tracking-wider mb-2">USD to LKR</p>
            <div className="grid grid-cols-3 gap-x-3 gap-y-2">
              <Detail label="Wise USD Sent" value={`$${fmt(event.wise_usd_sent)}`} />
              <Detail label="Fee USD" value={`$${fmt(event.fee_usd)}`} />
              <Detail label="Exchange Rate" value={`${fmt(event.exchange_rate_lkr_usd)} LKR/USD`} />
            </div>
            {lkrReceived > 0 && (
              <div className="bg-teal-500/10 border border-teal-100 rounded-lg px-3 py-1.5 mt-2 flex items-center justify-between">
                <div>
                  <p className="text-[9px] font-medium text-teal-600">Final LKR Received</p>
                  <p className="text-sm font-bold text-teal-400">LKR {fmt(lkrReceived)}</p>
                </div>
                <p className="text-[9px] text-teal-500">({fmt(event.wise_usd_sent)} - {fmt(event.fee_usd)}) x {fmt(event.exchange_rate_lkr_usd)}</p>
              </div>
            )}
          </div>

          <div>
            <p className="text-[9px] font-bold text-white uppercase tracking-wider mb-2">LKR to USDT</p>
            <div className="grid grid-cols-2 gap-x-3 gap-y-2">
              <Detail label="P2P Rate" value={`${fmt(event.p2p_rate_lkr_usdt)} LKR/USDT`} />
              <Detail label="USDT Bought" value={`${fmt(usdtBought)} USDT`} highlight="sky" />
            </div>
          </div>

          <div>
            <p className="text-[9px] font-bold text-white uppercase tracking-wider mb-2">USDT to MVR</p>
            <div className="grid grid-cols-2 gap-x-3 gap-y-2">
              <Detail label="USDT Sent" value={`${fmt(event.usdt_sent)} USDT`} />
              <Detail label="MVR Rate" value={`${fmt(event.mvr_rate_per_usdt)} MVR/USDT`} />
            </div>
            {mvrOut > 0 && (
              <div className="bg-emerald-500/10 border border-emerald-100 rounded-lg px-3 py-1.5 mt-2 flex items-center justify-between">
                <div>
                  <p className="text-[9px] font-medium text-emerald-400">Final MVR Out</p>
                  <p className="text-sm font-bold text-emerald-400">MVR {fmt(mvrOut)}</p>
                </div>
                <p className="text-[9px] text-emerald-400">{fmt(event.usdt_sent)} USDT x {fmt(event.mvr_rate_per_usdt)}</p>
              </div>
            )}
          </div>

          <div className="border-t border-white/10 pt-2">
            <div className="flex items-center justify-between">
              <p className="text-[9px] font-bold text-white uppercase tracking-wider">Net Profit</p>
              <p className="text-[9px] text-white">Bank rate: {fmt(bankRate)}</p>
            </div>
            <p className={`text-sm font-bold mt-0.5 ${profit >= 0 ? 'text-emerald-400' : 'text-red-700'}`}>
              MVR {fmt(profit)}
            </p>
          </div>

          {event.notes && (
            <div className="border-t border-white/10 pt-2">
              <p className="text-[9px] font-bold text-white uppercase tracking-wider mb-1">Notes</p>
              <p className="text-xs text-white whitespace-pre-wrap leading-relaxed">{event.notes}</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-white/10">
          <div>
            {canEdit && (
              <button
                onClick={onDelete}
                className="px-4 py-1.5 text-xs bg-red-500/10 text-red-600 rounded-lg glass-hover-red font-medium"
              >
                Delete
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-1.5 text-xs glass-button text-white rounded-lg font-medium"
            >
              Close
            </button>
            {canEdit && (
              <button
                onClick={onEdit}
                className="px-4 py-1.5 text-xs glass-button-active text-white rounded-lg font-medium"
              >
                Edit
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value, highlight }: { label: string; value: string; highlight?: 'sky' | 'teal' | 'emerald' }) {
  const colors = highlight
    ? {
        sky: 'text-sky-400 bg-sky-500/10 border-sky-100',
        teal: 'text-teal-400 bg-teal-500/10 border-teal-100',
        emerald: 'text-emerald-400 bg-emerald-500/10 border-emerald-100',
      }[highlight]
    : '';

  return (
    <div className={highlight ? `rounded-lg px-2 py-1 border ${colors}` : ''}>
      <p className="text-[9px] text-white font-medium">{label}</p>
      <p className={`text-xs font-semibold ${highlight ? '' : 'text-white'}`}>{value}</p>
    </div>
  );
}
